import { useState, useEffect } from 'react';
import { Clock, ClipboardCheck, Home, Syringe, Phone, ChevronLeft } from 'lucide-react';

export const emptyPreArrival = () => ({
  instructions: {},
  scene: {},
  info: { age: '', medications: '', allergies: '', antidote: '', notes: '' },
});

export const SCENE_CHECKS = [
  'Porte déverrouillée / accès dégagé',
  'Animaux sécurisés (enfermés)',
  'Lumières extérieures allumées',
  'Quelqu\'un envoyé pour guider les paramédics',
  'Objets dangereux écartés (seringues, armes, verre...)',
];

export const formatChrono = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');

export const buildPreArrivalPrintHtml = (preArrival, p1Seconds, totalInstructions) => {
  if (!preArrival || p1Seconds == null) return '';
  const instr = Object.entries(preArrival.instructions || {}).sort((a, b) => a[1] - b[1]);
  const scene = Object.entries(preArrival.scene || {}).sort((a, b) => a[1] - b[1]);
  const info = preArrival.info || {};
  const infoLabels = { age: 'Âge exact', medications: 'Médicaments', allergies: 'Allergies', antidote: 'Naloxone / EpiPen sur place', notes: 'Notes pour paramédics' };
  const infoRows = Object.entries(infoLabels)
    .map(([k, label]) => ({ label, value: (info[k] || '').trim() }))
    .filter(e => e.value);
  const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  let html = '<div style="margin-top:16px;padding:12px;border-left:4px solid #DC2626;background:#FFF7ED;border-radius:8px;">'
    + '<h4 style="color:#9A3412;font-size:14px;font-weight:bold;margin-bottom:8px;">🚑 Volet P1 — Consignes pré-arrivée &amp; informations paramédics</h4>'
    + '<p style="font-size:13px;margin-bottom:6px;"><strong>Délai P1 → fin d\'appel :</strong> ' + formatChrono(p1Seconds) + '</p>'
    + '<p style="font-size:13px;margin-bottom:6px;"><strong>Consignes données :</strong> ' + instr.length + ' / ' + (totalInstructions || instr.length) + '</p>';
  if (instr.length) {
    html += '<ul style="list-style:none;padding:0;margin-bottom:6px;">'
      + instr.map(([label, at]) => '<li style="font-size:12px;color:#7C2D12;padding:1px 0;">✓ ' + esc(label) + ' <em style="color:#9CA3AF;">(à ' + formatChrono(at) + ' après P1)</em></li>').join('')
      + '</ul>';
  }
  html += '<p style="font-size:13px;margin-bottom:6px;"><strong>Sécurisation des lieux :</strong> ' + scene.length + ' / ' + SCENE_CHECKS.length + '</p>';
  if (scene.length) {
    html += '<ul style="list-style:none;padding:0;margin-bottom:6px;">'
      + scene.map(([label, at]) => '<li style="font-size:12px;color:#7C2D12;padding:1px 0;">✓ ' + esc(label) + ' <em style="color:#9CA3AF;">(à ' + formatChrono(at) + ')</em></li>').join('')
      + '</ul>';
  }
  if (infoRows.length) {
    html += '<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:4px;">'
      + infoRows.map(e => '<tr><td style="padding:2px 8px 2px 0;font-weight:600;color:#7C2D12;width:40%;vertical-align:top;">' + esc(e.label) + '</td><td style="padding:2px 0;color:#1e293b;">' + esc(e.value) + '</td></tr>').join('')
      + '</table>';
  }
  html += '</div>';
  return html;
};

const PreArrivalPhase = ({ protocol, p1TriggeredAt, preArrival, setPreArrival, onEndCall, onBack, compact = false }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsed = () => (p1TriggeredAt ? Math.floor((Date.now() - p1TriggeredAt) / 1000) : 0);
  const consignes = protocol?.priorities?.['1']?.pre_arrival || [];

  const toggle = (section, label) => {
    setPreArrival(prev => {
      const map = { ...prev[section] };
      if (map[label] !== undefined) delete map[label];
      else map[label] = elapsed();
      return { ...prev, [section]: map };
    });
  };

  const setInfo = (key, value) => {
    setPreArrival(prev => ({ ...prev, info: { ...prev.info, [key]: value } }));
  };

  const txt = compact ? 'text-xs' : 'text-sm';
  const txtSm = compact ? 'text-[11px]' : 'text-xs';
  const givenCount = Object.keys(preArrival.instructions).length;
  const sceneCount = Object.keys(preArrival.scene).length;

  const CheckItem = ({ section, label, testId }) => {
    const at = preArrival[section][label];
    const checked = at !== undefined;
    return (
      <button
        type="button"
        onClick={() => toggle(section, label)}
        className={`w-full flex items-start gap-2 p-2 rounded-lg border text-left transition-all ${txt}`}
        style={{
          borderColor: checked ? '#16A34A' : '#E5E7EB',
          backgroundColor: checked ? '#F0FDF4' : 'white',
        }}
        data-testid={testId}
      >
        <span
          className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5"
          style={{ backgroundColor: checked ? '#16A34A' : '#E5E7EB', color: checked ? 'white' : '#9CA3AF' }}
        >
          {checked ? '✓' : ''}
        </span>
        <span className={`flex-1 ${checked ? 'text-green-800 font-medium' : 'text-gray-700'}`}>{label}</span>
        {checked && <span className={`${txtSm} text-green-600 font-mono flex-shrink-0`}>{formatChrono(at)}</span>}
      </button>
    );
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'} data-testid="pre-arrival-phase">
      {/* Header + chrono */}
      <div className="p-3 rounded-xl border-2 flex items-center justify-between gap-2" style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
        <div>
          <h3 className={`font-bold text-red-700 ${compact ? 'text-sm' : 'text-base'}`}>🚑 Consignes pré-arrivée &amp; paramédics</h3>
          <p className={`${txtSm} text-red-600`}>Guidez le témoin en attendant l'ambulance. Cochez chaque consigne donnée.</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`${txtSm} text-red-500 flex items-center gap-1 justify-end`}><Clock className="w-3 h-3" /> Depuis P1</div>
          <div className={`font-mono font-bold text-red-700 ${compact ? 'text-base' : 'text-lg'}`} data-testid="pre-arrival-chrono">{formatChrono(elapsed())}</div>
        </div>
      </div>

      {onBack && (
        <button type="button" onClick={onBack} className={`flex items-center gap-1 ${txtSm} text-gray-500 hover:text-gray-800`} data-testid="pre-arrival-back-btn">
          <ChevronLeft className="w-3 h-3" /> Retour au protocole
        </button>
      )}

      {/* Consignes du protocole */}
      <div>
        <h4 className={`${txt} font-bold text-gray-700 mb-1.5 flex items-center gap-1.5`}>
          <ClipboardCheck className="w-4 h-4 text-red-600" />
          Consignes pré-arrivée du protocole
          <span className={`${txtSm} font-semibold px-1.5 py-0.5 rounded-full ${givenCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`} data-testid="pre-arrival-instructions-count">
            {givenCount}/{consignes.length}
          </span>
        </h4>
        <div className="space-y-1">
          {consignes.map((c, idx) => (
            <CheckItem key={c} section="instructions" label={c} testId={`pre-arrival-instruction-${idx}`} />
          ))}
        </div>
      </div>

      {/* Sécurisation des lieux */}
      <div>
        <h4 className={`${txt} font-bold text-gray-700 mb-1.5 flex items-center gap-1.5`}>
          <Home className="w-4 h-4 text-blue-600" />
          Sécurisation des lieux
          <span className={`${txtSm} font-semibold px-1.5 py-0.5 rounded-full ${sceneCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {sceneCount}/{SCENE_CHECKS.length}
          </span>
        </h4>
        <div className="space-y-1">
          {SCENE_CHECKS.map((c, idx) => (
            <CheckItem key={c} section="scene" label={c} testId={`scene-check-${idx}`} />
          ))}
        </div>
      </div>

      {/* Informations pour les paramédics */}
      <div>
        <h4 className={`${txt} font-bold text-gray-700 mb-1.5 flex items-center gap-1.5`}>
          <Syringe className="w-4 h-4 text-amber-600" />
          Informations pour les paramédics
        </h4>
        <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
          <div>
            <label className={`${txtSm} font-semibold text-gray-500`}>Âge exact du patient</label>
            <input type="text" value={preArrival.info.age} onChange={e => setInfo('age', e.target.value)}
              placeholder="Ex : 67 ans" className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400 ${txt}`}
              data-testid="paramedic-age-input" />
          </div>
          <div>
            <label className={`${txtSm} font-semibold text-gray-500`}>Médicaments pris / à proximité</label>
            <input type="text" value={preArrival.info.medications} onChange={e => setInfo('medications', e.target.value)}
              placeholder="Ex : nitro, anticoagulants..." className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400 ${txt}`}
              data-testid="paramedic-medications-input" />
          </div>
          <div>
            <label className={`${txtSm} font-semibold text-gray-500`}>Allergies connues</label>
            <input type="text" value={preArrival.info.allergies} onChange={e => setInfo('allergies', e.target.value)}
              placeholder="Ex : pénicilline, aucune connue..." className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400 ${txt}`}
              data-testid="paramedic-allergies-input" />
          </div>
          <div>
            <label className={`${txtSm} font-semibold text-gray-500`}>Naloxone / EpiPen sur place ?</label>
            <div className="flex gap-1 mt-0.5">
              {['Oui', 'Non', 'Inconnu'].map(val => {
                const selected = preArrival.info.antidote === val;
                return (
                  <button key={val} type="button" onClick={() => setInfo('antidote', selected ? '' : val)}
                    className={`flex-1 py-1.5 rounded-lg font-semibold border transition-all ${txtSm}`}
                    style={{
                      borderColor: selected ? '#2E6CF5' : '#E5E7EB',
                      backgroundColor: selected ? '#EFF6FF' : 'white',
                      color: selected ? '#2E6CF5' : '#6B7280',
                    }}
                    data-testid={`antidote-${val.toLowerCase()}`}>
                    {val}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={compact ? '' : 'col-span-2'}>
            <label className={`${txtSm} font-semibold text-gray-500`}>Notes pour paramédics (accès, étage, code, particularités...)</label>
            <textarea value={preArrival.info.notes} onChange={e => setInfo('notes', e.target.value)} rows={2}
              placeholder="Ex : 3e étage sans ascenseur, code porte 1234..." className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400 ${txt}`}
              data-testid="paramedic-notes-input" />
          </div>
        </div>
      </div>

      {/* End call */}
      <button
        type="button"
        onClick={() => {
          if (window.confirm('Terminer l\'appel maintenant ? Le résumé de session sera généré.')) onEndCall();
        }}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-white hover:opacity-90 transition ${txt}`}
        style={{ backgroundColor: '#DC2626' }}
        data-testid="pre-arrival-end-call-btn"
      >
        <Phone className="w-4 h-4" /> 📞 Terminer l'appel — secours arrivés
      </button>
    </div>
  );
};

export default PreArrivalPhase;
